import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { NativeArticle } from "@/components/site/NativeArticle";
import { ArrowLeft } from "lucide-react";

/**
 * Full-site preview of an UNSAVED article draft. The admin News editor writes
 * the draft to localStorage under PREVIEW_KEY and opens this route in a new
 * tab — same origin, so the draft crosses over without any server round trip
 * and the article appears inside the real site chrome (top bar, theme,
 * footer) exactly as it will publish. Static routes outrank /news/$slug, so
 * "preview" is never treated as a slug. Not in the nav or sitemap; the
 * prerenderer skips pages that render empty, which this is without a draft.
 */

export const PREVIEW_KEY = "mii-article-preview";

export type ArticlePreviewDraft = {
  title: string;
  excerpt: string | null;
  body_html: string;
  published_at: string;
  body_format: "email" | "clean" | null;
  /** The newsletter lead graphic — list cards and the hero fallback. */
  image_url: string | null;
  /** The wide header graphic, when the issue has one (optional: older
      staged drafts predate the field). */
  header_image_url?: string | null;
  /** news_articles.show_advisory_board (optional: older staged drafts
      predate the field, and they showed the board). */
  show_advisory_board?: boolean;
};

function looksLikeEmailHtml(body: string) {
  const head = body.slice(0, 2000).toLowerCase();
  return head.includes("<table") || head.includes("xmlns:o=") || head.includes("<!doctype");
}

export const Route = createFileRoute("/news_/preview")({
  head: () => ({ meta: [{ title: "Article preview — Medical Insight" }] }),
  component: PreviewPage,
});

function PreviewPage() {
  const [draft, setDraft] = useState<ArticlePreviewDraft | null | undefined>(undefined);
  const frameRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(PREVIEW_KEY);
      setDraft(raw ? (JSON.parse(raw) as ArticlePreviewDraft) : null);
    } catch {
      setDraft(null);
    }
  }, []);

  if (draft === undefined) return null;

  if (draft === null || !draft.body_html) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold">No draft to preview</h1>
        <p className="mt-2 text-muted-foreground">
          Open an article in Admin &gt; News &amp; Articles and use "Preview website".
        </p>
        <Button asChild className="mt-6">
          <Link to="/news">Back to Aesthetic Insights</Link>
        </Button>
      </div>
    );
  }

  const format =
    draft.body_format === "email" || draft.body_format === "clean"
      ? draft.body_format
      : looksLikeEmailHtml(draft.body_html)
        ? "email"
        : "clean";

  return (
    <div className="border-t bg-secondary/30">
      <div className="border-b border-amber-300 bg-amber-50 px-4 py-2 text-center text-sm text-amber-800">
        Preview of an unsaved draft — this page is only visible to you.
      </div>
      <div className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
        <div className="mb-4 flex items-center justify-between gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link to="/news">
              <ArrowLeft className="mr-1 h-4 w-4" /> Aesthetic Insights
            </Link>
          </Button>
          {format === "email" && (
            <span className="text-sm text-muted-foreground">
              {draft.published_at
                ? new Date(`${draft.published_at.slice(0, 10)}T00:00:00`).toLocaleDateString(
                    undefined,
                    { year: "numeric", month: "long", day: "numeric" },
                  )
                : ""}
            </span>
          )}
        </div>

        {format === "email" ? (
          <>
            <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
              {draft.title || "Untitled"}
            </h1>
            {draft.excerpt && (
              <p className="mt-3 max-w-3xl text-muted-foreground">{draft.excerpt}</p>
            )}
            <div className="mt-8 overflow-hidden rounded-lg border bg-white">
              <iframe
                ref={frameRef}
                title="Article preview"
                sandbox="allow-same-origin"
                srcDoc={draft.body_html}
                className="w-full"
                style={{ minHeight: "60vh", border: 0 }}
                onLoad={() => {
                  const f = frameRef.current;
                  const doc = f?.contentDocument;
                  if (!f || !doc) return;
                  const fit = () => {
                    f.style.height = `${Math.max(doc.documentElement.scrollHeight, 400)}px`;
                  };
                  fit();
                  setTimeout(fit, 500);
                  setTimeout(fit, 2000);
                }}
              />
            </div>
          </>
        ) : (
          <NativeArticle
            title={draft.title || "Untitled"}
            excerpt={draft.excerpt}
            published_at={draft.published_at}
            image_url={draft.image_url}
            header_image_url={draft.header_image_url ?? null}
            body_html={draft.body_html}
            showBoard={draft.show_advisory_board !== false}
          />
        )}
      </div>
    </div>
  );
}
